import { createAsyncThunk } from "@reduxjs/toolkit";
import { ResourceTarget } from "@/services/cloudchipr.api";
import {
  initiateResourceDetailsDrawerThunk,
  InitiateResourceDetailsDrawerThunkArgs,
} from "@/store/resource-details/thunks/initiateResourceDetailsDrawerThunk";
import { money } from "@/utils/numeral/money";
import { isTaskOpportunityTarget } from "@/store/task-management/utils/helpers/isTaskOpportunityTarget";
import { ResourceDetailsActiveTab } from "@/store/resource-details/utils/types/common";
import { ResourceAdditionalDetailsMap } from "@/store/resource-details/utils/types/resourceAdditionalDetails";
import { listingPriceColumnDefField } from "@/components/pages/common/resources-ag-grid/components/column-defs/common/listingPriceColumnDef";
import { TaskOpportunityTarget } from "@/store/task-management/utils/types/types";
import { resourceTypesThatCanHaveCoverage } from "@/components/pages/common/resource-details/utils/constants/resourceTypesThatCanHaveCoverage";
import { getResourceExplorerSingleResourceBillingDataThunk } from "@/store/resource-explorer/thunks/resource-explorer/getResourceExplorerSingleResourceBillingDataThunk";
import { getResourceDetailsSingleResourceDataThunk } from "@/store/resource-details/thunks/data/getResourceDetailsSingleResourceDataThunk";
import { AllTypeResourceItem } from "@/store/resources-grid/utils/types/resources";
import { enqueueSnackbarErrorAlert } from "@/components/pages/common/snackbar-alert/EnqueueSnackbarErrorAlert";

export const setTasksResourceDetailsDrawerResourceDataThunk = createAsyncThunk(
  "tasks/setTasksResourceDetailsDrawerResourceData",
  async (target: ResourceTarget | TaskOpportunityTarget, { dispatch }) => {
    if (!target.resource_type || !target.account) {
      return;
    }

    const initialArgs =
      getInitiateResourceDetailsDrawerThunkArgsFromTaskTarget(target);

    if (!initialArgs) {
      return;
    }

    dispatch(initiateResourceDetailsDrawerThunk(initialArgs));

    try {
      const resourceBillingData = await dispatch(
        getResourceExplorerSingleResourceBillingDataThunk({
          provider: target.account.provider,
          accountId: target.account.id,
          resourceId: target.provider_identifier,
        }),
      ).unwrap();

      const resource = await dispatch(
        // get and apply single resource data
        getResourceDetailsSingleResourceDataThunk({
          provider: target.account.provider,
          accountId: target.account.id,
          resourceId: target.provider_identifier,
        }),
      ).unwrap();

      if (!resource) {
        return;
      }

      dispatch(
        initiateResourceDetailsDrawerThunk({
          ...initialArgs,
          tags: {
            loading: false,
            tags: resourceBillingData?.tags ?? initialArgs?.tags?.tags ?? [],
          },
          resourceAdditionalDetails: {
            resource: resource as unknown as AllTypeResourceItem,
          },
        }),
      );
    } catch (e) {
      // @ts-expect-error todo: fix api spec;
      enqueueSnackbarErrorAlert(e?.data?.message);
    }
  },
);

const getInitiateResourceDetailsDrawerThunkArgsFromTaskTarget = (
  target: ResourceTarget | TaskOpportunityTarget,
): InitiateResourceDetailsDrawerThunkArgs | undefined => {
  const formatedForestedEOM = money(target.forecasted_end_of_month);

  if (!target.account || !target.resource_type) {
    return;
  }

  let details: ResourceAdditionalDetailsMap[] = [
    { title: "Resource Type", value: target.resource_type },
    { title: "Region", value: target.region },
    {
      title: "Forecasted EOM",
      value:
        formatedForestedEOM !== "N/A"
          ? `~${formatedForestedEOM}`
          : formatedForestedEOM,
    },
    {
      title: "Billing Cost",
      value: money(target.billing_cost?.total),
    },
    {
      title: "Listing Price",
      value: {
        customComponentIdentifier: listingPriceColumnDefField,
        customComponentProps: {
          listing_price: target.listing_price,
        },
      },
    },
    {
      title: "Account",
      value:
        target.account.provider_account_name ||
        target.account.provider_account_id,
    },
  ];

  if (isTaskOpportunityTarget(target)) {
    details = [
      ...details,
      { title: "Resource ARN", value: target.resource_arn ?? "" },
    ];
  } else {
    details = [
      ...details,
      { title: "State", value: target.state ?? "" },
      {
        title: "Created By",
        value: target?.creation_data?.creator ?? "",
      },
    ];
  }

  const availableTabs: ResourceDetailsActiveTab[] = [
    "general",
    "costs",
    "metrics",
    "opportunities",
  ];

  if (
    ["aws", "azure"].includes(target.account.provider) &&
    resourceTypesThatCanHaveCoverage.has(target.resource_type)
  ) {
    availableTabs.push("coverage");
  }

  return {
    openedFrom: "savingsOpportunities",
    availableTabs,
    tags: { loading: false, tags: target.tags ?? [] },
    resource: {
      region: target.region,
      provider: target.account.provider,
      providerAccountId: target.account.provider_account_id,
      name: target.provider_identifier,
      providerAccountName: target.account.provider_account_name,
      accountId: target.account.id,
      provider_unique_identifier: target.provider_identifier,
      billing_explorer_resource_identifier: [target.provider_identifier],
      resourceType: target.resource_type,
    },
    resourceAdditionalDetails: { details },
  };
};
